<script setup lang="ts">
import { ref, reactive, watch, onMounted, onUnmounted } from "vue";
import { flushArray } from "@zyzgroup/core-common";
import { useSafeArea$ } from "@zyzgroup/core-web";
import Pop from "../pop/Pop.vue";
import SVGCleanLeft from "../svg/SVGCleanLeft.vue";
import Box from "../base/Box.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Pop, SVGCleanLeft }
});

const props = withDefaults(
  defineProps<{
    initPlates?: string[];
    completePlateLength?: number;
  }>(),
  {
    initPlates: () => [],
    completePlateLength: 7
  }
);

const emits = defineEmits<{
  (event: "open"): void;
  (event: "close"): void;
  (event: "resize", rect: DOMRect): void;
  (event: "plates", plates: string[]): void;
}>();

const safeAreaBottom = ref("0px");
let subcription;
onMounted(() => {
  subcription = useSafeArea$().subscribe((s) => {
    safeAreaBottom.value = s.bottom;
  });
});
onUnmounted(() => {
  subcription?.unsubscribe();
});

const FIRST = [
  "京",
  "沪",
  "津",
  "渝",
  "黑",
  "吉",
  "辽",
  "蒙",
  "冀",
  "新",
  "甘",
  "青",
  "陕",
  "宁",
  "豫",
  "鲁",
  "晋",
  "皖",
  "鄂",
  "湘",
  "苏",
  "川",
  // "黔",
  // "滇",
  "云",
  "桂",
  "藏",
  "浙",
  "赣",
  "粤",
  "闽",
  "琼",
  "贵"
];
const NUMBER = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];
const ENGLISH = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "J",
  "K",
  "L",
  "M",
  "N",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z"
];
const CHINESE = [
  // "台",
  "港",
  "澳",
  "学",
  "警",
  "使",
  "领",
  "挂",
  "应",
  "急"
];

const popRef = ref();

// 0 中文 1 英文 2 英文+数字+中文
const keyboardStep = ref(0);

const plates = reactive<string[]>([]);

function platesChangeCallback() {
  if (plates.length == 0) {
    keyboardStep.value = 0;
  } else if (plates.length == 1) {
    keyboardStep.value = 1;
  } else {
    keyboardStep.value = 2;
  }
  if (plates.length >= props.completePlateLength) {
    close();
  }
}

watch(
  () => [...props.initPlates],
  () => {
    flushArray(plates, props.initPlates);
    platesChangeCallback();
  },
  {
    immediate: true
  }
);

const open = () => {
  popRef.value?.open();
};

const close = () => {
  popRef.value?.close();
};

const back = () => {
  plates.pop();
  platesChangeCallback();
  emits("plates", plates);
};

const click = (e: Event) => {
  const dataset = ((e.currentTarget || e.target) as HTMLElement).dataset;
  if (!!dataset.unactive) {
    return;
  }
  let number = dataset.number;
  if (number) {
    plates.push(number);
    platesChangeCallback();
    emits("plates", plates);
  }
};

defineExpose({ open, close });
</script>

<template>
  <Pop
    ref="popRef"
    directionFrom="bottom"
    :global="true"
    :closeWhenClickOverlay="false"
    @open="() => emits('open')"
    @close="() => emits('close')"
    @resize="(rect) => emits('resize', rect)"
  >
    <div>
      <div
        class="popup-plate"
        :style="{
          paddingBottom: safeAreaBottom
        }"
      >
        <div class="popup-plate-close">
          <Box
            class="popup-plate-close-content"
            displayType="empty"
            :enableClickEffect="true"
            @throttleClick="close"
          >
            关闭
          </Box>
        </div>
        <div
          class="step"
          :class="keyboardStep == 0 ? '' : 'hide'"
        >
          <Box
            v-for="item in FIRST"
            :key="item"
            class="popitem"
            displayType="empty"
            :enableClickEffect="true"
            @throttleClick="click"
            :data-number="item"
          >
            {{ item }}
          </Box>
          <Box
            class="popitem popitem-clear"
            displayType="empty"
            :enableClickEffect="true"
            @throttleClick="back"
          >
            <div style="width: 16px">
              <SVGCleanLeft></SVGCleanLeft>
            </div>
          </Box>
        </div>
        <div
          class="step"
          :class="keyboardStep != 0 ? '' : 'hide'"
        >
          <Box
            v-for="item in NUMBER"
            :key="item"
            class="popitem"
            :class="keyboardStep != 2 ? 'unactive' : ''"
            displayType="empty"
            :enableClickEffect="true"
            @throttleClick="click"
            :data-number="item"
            :data-unactive="keyboardStep != 2 ? 'unactive' : ''"
          >
            {{ item }}
          </Box>
          <Box
            v-for="item in ENGLISH"
            :key="item"
            class="popitem"
            displayType="empty"
            :enableClickEffect="true"
            @throttleClick="click"
            :data-number="item"
          >
            {{ item }}
          </Box>
          <Box
            v-for="item in CHINESE"
            :key="item"
            class="popitem"
            :class="keyboardStep != 2 ? 'unactive' : ''"
            displayType="empty"
            :enableClickEffect="true"
            @throttleClick="click"
            :data-number="item"
            :data-unactive="keyboardStep != 2 ? 'unactive' : ''"
          >
            {{ item }}
          </Box>
          <Box
            class="popitem popitem-clear"
            displayType="empty"
            :enableClickEffect="true"
            @throttleClick="back"
          >
            <div style="width: 16px">
              <SVGCleanLeft></SVGCleanLeft>
            </div>
          </Box>
        </div>
      </div>
    </div>
  </Pop>
</template>

<style scoped>
.popup-plate {
  box-sizing: border-box;
  width: 100%;
  background-color: #dddddd;
  color: #333333;
}
.hide {
  display: none !important;
}
.popup-plate-close {
  display: flex;
  justify-content: flex-end;
  background-color: #ffffff;
  border-top: 1px solid #e6e6e6;
}
.popup-plate-close-content {
  flex: 0 0 auto;
  padding: 0 15px;
  color: #5a82f0;
  font-size: 16px;
  line-height: 2.6;
  cursor: pointer;
}
.step {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding: 10px 10px 5px;
}
.popitem {
  flex: 0 0 auto;
  width: 50px;
  padding: 010px 0;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  margin-right: 5px;
  margin-bottom: 5px;
  font-size: 16px;
  line-height: 1;
  text-align: center;
  background-color: #fff;
  cursor: pointer;
}
.popitem:not(.unactive):active {
  background-color: #eee;
}
.popitem.unactive {
  color: #cccccc;
}
.popitem.popitem-clear {
  background-color: #eee;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
